import React from "react";
import ReactDOM from "react-dom/client";
import { ConvexReactClient } from "convex/react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { ClerkProvider, useAuth } from "@clerk/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import "./styles/tokens.css";
/**
 * Imported for its side effect, and above the router on purpose: it reads
 * `?code=` out of the URL at module load, which has to happen before
 * `RouterProvider` mounts and `/join` redirects to `/tasks` and takes the query
 * string with it. See `lib/invite.ts`.
 */
import "./lib/invite";
import { routeTree } from "./routeTree.gen";
import { SessionProvider } from "./components/SessionProvider";

const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL as string);

const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

/**
 * Providers, outermost first, and the order is load-bearing:
 *
 *  - `ClerkProvider` owns the identity.
 *  - `ConvexProviderWithClerk` is what puts Clerk's token on every Convex
 *    request. A plain `ConvexProvider` would still work and still connect —
 *    it just would not authenticate anything, so every query would throw.
 *  - `SessionProvider` turns "signed in" into "in this household, as this
 *    member". Outside the router, because that has the same lifetime as the
 *    Convex subscriptions and must not be torn down on every navigation.
 *
 * `VITE_CLERK_PUBLISHABLE_KEY` is read at build time. On Cloudflare that means
 * a *build* variable — the runtime kind resolves to an empty value, which
 * fails as a key that exists and is rejected rather than as a missing one.
 */
ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ClerkProvider publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string}>
      <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
        <SessionProvider>
          <RouterProvider router={router} />
        </SessionProvider>
      </ConvexProviderWithClerk>
    </ClerkProvider>
  </React.StrictMode>,
);
